import { Component } from "react";
import { useLocation } from "react-router-dom";

/**
 * The global error boundary (H-4).
 *
 * ── WHAT IT IS FOR ──────────────────────────────────────────────────────────
 * Before this existed, ANY exception thrown while rendering took the whole panel to a blank white
 * screen with no message, no recovery and nothing in the UI to act on. `ErrorState` handles a FAILED
 * REQUEST — a rejected query, which react-query turns into data the page can render. It cannot help
 * when the render itself throws, because at that point there is no page left to show it.
 *
 * ── WHAT A BOUNDARY DOES AND DOES NOT CATCH (React's rule, not ours) ────────
 * CAUGHT   — exceptions thrown during render, in lifecycle methods, and in constructors of the
 *            subtree below it. That includes a REJECTED `React.lazy()` import, which is how a stale
 *            chunk after a deploy surfaces.
 * NOT      — errors inside event handlers, inside `setTimeout`, and rejected promises that nobody
 * CAUGHT     awaits. Those never enter React's render path. In this panel every network call goes
 *            through react-query, which captures rejections and exposes them as `isError` for
 *            `ErrorState` to render — so the uncaught-async gap is already covered by the existing
 *            architecture and no global `unhandledrejection` hook is added here.
 *
 * ── TWO LEVELS, DELIBERATELY ────────────────────────────────────────────────
 * `App.jsx` wraps everything: it is the last resort, and it is the only thing that can catch a crash
 * in the chrome itself (sidebar, header, the auth provider's own render).
 * `AdminLayout` wraps the `<Outlet/>`: a page that crashes leaves the sidebar and header standing, so
 * the user can simply navigate somewhere else instead of reloading.
 *
 * ── RECOVERY ────────────────────────────────────────────────────────────────
 * `RouteErrorBoundary` is keyed by pathname, so NAVIGATING AWAY CLEARS THE ERROR by remounting the
 * boundary — no manual reset, and no stale error stuck over a page that is fine.
 * "Try again" re-renders the same subtree, which is the right remedy for a transient failure.
 * A CHUNK-LOAD failure is different and is detected separately: retrying re-requests a URL that no
 * longer exists, so that case offers a reload instead, which fetches the new index.html.
 *
 * Nothing here touches the auth context, the query cache or the router — an error unmounts the failed
 * subtree only, so the session, the cached data and the current URL all survive.
 */

/** A stale-deploy chunk failure, which a re-render cannot fix but a reload can. */
function isChunkLoadError(error) {
  const message = String(error?.message || "");
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    error?.name === "ChunkLoadError"
  );
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // There is no error-reporting service in this stack, so the console is the only sink. Logged
    // with the component stack because that is the part a stack trace alone does not give you.
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunk = isChunkLoadError(error);

    return (
      <div className="p-4">
        <div className="alert alert-danger" role="alert" style={{ maxWidth: 720 }}>
          <div className="d-flex align-items-start gap-2">
            <i className="bi bi-exclamation-octagon mt-1" />
            <div className="flex-grow-1">
              <div className="fw-semibold">
                {chunk ? "This page could not be loaded" : "Something went wrong on this screen"}
              </div>

              <div className="small mt-1">
                {chunk ? (
                  <>
                    The application was updated while this tab was open, so part of it is no longer
                    available. Reloading will pick up the new version.
                  </>
                ) : (
                  <>
                    The error has been logged to the browser console. Your session is still active and
                    nothing you had already saved is affected — you can go to another page and carry on.
                  </>
                )}
              </div>

              {/* The message itself, not a generic apology — the same principle ErrorState follows. */}
              <div className="small text-muted mt-2" style={{ whiteSpace: "pre-wrap" }}>
                {String(error?.message || error)}
              </div>

              <div className="d-flex gap-2 mt-3">
                {chunk ? (
                  <button className="btn btn-sm btn-danger" onClick={() => window.location.reload()}>
                    <i className="bi bi-arrow-clockwise me-1" /> Reload
                  </button>
                ) : (
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => this.setState({ error: null })}
                  >
                    <i className="bi bi-arrow-clockwise me-1" /> Try again
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * The route-level boundary: identical, but remounted on every navigation.
 *
 * The `key` is what makes recovery automatic. Without it a boundary that has caught an error stays in
 * its error state forever — React does not reset it — so the next page would render the previous
 * page's failure.
 */
export function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}
